import { CardsThreeIcon, CheckIcon, FolderIcon, PlusIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ResponsiveMenuSub } from '@/components/projects/ResponsiveMenuSub';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  useAddMediaToProjects,
  useAssignableProjects,
  useMediaProjectAssignments,
} from '@/hooks/projects/useProjectAssignments';
import { formatWhen } from '@/lib/format-when';
import { MENU_ITEM_MOBILE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { campaignService } from '@/services/data/CampaignService';
import type { ProjectRow } from '@/services/data/ProjectAssignmentService';
import { getProjectColor, getProjectIcon } from '@/utils/project-identity';

interface ProjectAssignmentBaseProps {
  mediaIds: string[];
  skippedCount?: number;
  onAssigned?: (projectId: string, projectName: string) => void;
}

interface ProjectAssignmentSubmenuProps extends ProjectAssignmentBaseProps {
  label?: string;
}

interface ProjectAssignmentPopoverProps extends ProjectAssignmentBaseProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
}

export function ProjectAssignmentSubmenu({
  mediaIds,
  skippedCount = 0,
  onAssigned,
  label = 'Add to campaign',
}: ProjectAssignmentSubmenuProps): JSX.Element {
  const state = useProjectAssignmentState(mediaIds, skippedCount, onAssigned);

  return (
    <ResponsiveMenuSub icon={<FolderIcon className="size-4" weight="regular" />} label={label}>
      {state.projects.length === 0 && !state.loading ? (
        <div className="px-2 py-2 text-detail text-muted-foreground">No active campaigns</div>
      ) : null}
      {state.loading ? (
        <div className="px-2 py-2 text-detail text-muted-foreground">Loading campaigns...</div>
      ) : (
        state.projects.map((project) => (
          <ProjectDropdownItem
            key={project.id}
            project={project}
            checked={state.isChecked(project.id)}
            pending={state.pendingProjectId === project.id}
            onSelect={() => void state.assign(project)}
          />
        ))
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={state.createProject}
        className={cn(MENU_ITEM_MOBILE, 'min-h-11 rounded-sm md:min-h-0')}
      >
        <PlusIcon className="size-4" weight="regular" />
        <span>Create new campaign...</span>
      </DropdownMenuItem>
    </ResponsiveMenuSub>
  );
}

export function ProjectAssignmentPopover({
  mediaIds,
  skippedCount = 0,
  onAssigned,
  children,
  open: openProp,
  onOpenChange,
  align = 'end',
}: ProjectAssignmentPopoverProps): JSX.Element {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const state = useProjectAssignmentState(
    mediaIds,
    skippedCount,
    (projectId, projectName) => {
      onAssigned?.(projectId, projectName);
      setOpen(false);
    },
    open,
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children ?? (
          <Button size="sm" variant="outline">
            <CardsThreeIcon className="size-3.5" weight="regular" />
            Add to campaign
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-[min(18rem,var(--radix-popover-content-available-width))] p-0"
      >
        <ProjectPickerCommand
          projects={state.projects}
          loading={state.loading}
          isChecked={state.isChecked}
          pendingProjectId={state.pendingProjectId}
          onSelect={(projectId) => {
            const project = state.projects.find((p) => p.id === projectId);
            if (project) void state.assign(project);
          }}
          onCreateProject={state.createProject}
          onCreateProjectWithName={(name) => void state.createProjectAndAssign(name)}
        />
      </PopoverContent>
    </Popover>
  );
}

export interface ProjectPickerCommandProps {
  projects: Array<Pick<ProjectRow, 'id' | 'name' | 'icon' | 'color' | 'updated_at'>>;
  loading: boolean;
  isChecked?: (projectId: string) => boolean;
  pendingProjectId?: string | null;
  onSelect: (projectId: string) => void;
  onCreateProject: () => void;
  /**
   * Inline quick-create: when provided, the search input is ALWAYS rendered
   * (it doubles as the new project's name field) and a typed query turns the
   * footer row into `Create "<query>"` — create + assign without leaving the
   * picker. Without it, the footer row falls back to `onCreateProject`
   * (navigate to the plan composer), which discards the pending selection.
   */
  onCreateProjectWithName?: (name: string) => void;
}

/**
 * Shared Command-based "pick a project" list — used by every add-to-project
 * popover (Library selection bar, PostBody, SaveToProjectMenu's own-popover
 * branch). Rows follow the app's compact caption-menu convention: name
 * (flex-1 truncate) → updated-date disambiguator → check, with the date
 * hidden once a row is checked so the two trailing elements never collide.
 */
export function ProjectPickerCommand({
  projects,
  loading,
  isChecked,
  pendingProjectId,
  onSelect,
  onCreateProject,
  onCreateProjectWithName,
}: ProjectPickerCommandProps): JSX.Element {
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();
  return (
    <Command>
      {onCreateProjectWithName || projects.length > 6 ? (
        <CommandInput
          placeholder={
            onCreateProjectWithName ? 'Search or create project...' : 'Search projects...'
          }
          className="h-9 text-caption"
          value={query}
          onValueChange={setQuery}
        />
      ) : null}
      <CommandList>
        <CommandEmpty>{loading ? 'Loading campaigns...' : 'No active campaigns'}</CommandEmpty>
        <CommandGroup>
          {projects.map((project) => {
            const checked = isChecked?.(project.id) ?? false;
            const pending = pendingProjectId === project.id;
            return (
              <CommandItem
                key={project.id}
                value={`${project.name || 'Untitled project'}-${project.id}`}
                keywords={[project.name ?? '']}
                onSelect={() => onSelect(project.id)}
                disabled={pending}
                className="gap-2 rounded-sm px-2 py-1.5 text-caption"
              >
                <ProjectGlyph project={project} compact />
                <span
                  className="truncate flex-1 text-foreground/85"
                  title={project.name || undefined}
                >
                  {project.name || 'Untitled project'}
                </span>
                {checked || pending ? (
                  <CheckIcon className="size-3.5 shrink-0 text-foreground" weight="bold" />
                ) : project.updated_at ? (
                  <span className="whitespace-nowrap text-detail text-muted-foreground/70">
                    {formatWhen(project.updated_at)}
                  </span>
                ) : null}
              </CommandItem>
            );
          })}
          {onCreateProjectWithName && trimmedQuery ? (
            <CommandItem
              value={`create-${trimmedQuery}`}
              keywords={[trimmedQuery]}
              disabled={pendingProjectId === CREATING_PROJECT_ID}
              onSelect={() => onCreateProjectWithName(trimmedQuery)}
              className="gap-2 rounded-sm px-2 py-1.5 text-caption"
            >
              <PlusIcon className="size-3.5 shrink-0 text-muted-foreground/70" weight="regular" />
              <span className="truncate">
                {pendingProjectId === CREATING_PROJECT_ID
                  ? `Creating "${trimmedQuery}"...`
                  : `Create "${trimmedQuery}"`}
              </span>
            </CommandItem>
          ) : (
            <CommandItem
              value="Create new campaign"
              onSelect={onCreateProject}
              className="gap-2 rounded-sm px-2 py-1.5 text-caption"
            >
              <PlusIcon className="size-3.5 text-muted-foreground/70" weight="regular" />
              <span>Create new campaign...</span>
            </CommandItem>
          )}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

/** Sentinel pendingProjectId while an inline quick-create is in flight. */
const CREATING_PROJECT_ID = '__creating__';

function useProjectAssignmentState(
  rawMediaIds: string[],
  skippedCount: number,
  onAssigned?: (projectId: string, projectName: string) => void,
  enabled = true,
) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mediaIds = useMemo(() => [...new Set(rawMediaIds.filter(Boolean))], [rawMediaIds]);
  const projectsQuery = useAssignableProjects(enabled);
  const assignmentsQuery = useMediaProjectAssignments(mediaIds, enabled && mediaIds.length > 0);
  const addMedia = useAddMediaToProjects();
  const [pendingProjectId, setPendingProjectId] = useState<string | null>(null);

  const assignedProjectIds = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const assignment of assignmentsQuery.data ?? []) {
      const set = map.get(assignment.projectId) ?? new Set<string>();
      set.add(assignment.mediaId);
      map.set(assignment.projectId, set);
    }
    return map;
  }, [assignmentsQuery.data]);

  const isChecked = (projectId: string): boolean => {
    const assignedMedia = assignedProjectIds.get(projectId);
    return Boolean(
      assignedMedia && mediaIds.length > 0 && mediaIds.every((id) => assignedMedia.has(id)),
    );
  };

  const assign = async (project: ProjectRow): Promise<void> => {
    if (mediaIds.length === 0) {
      toast.error('No media selected');
      return;
    }
    setPendingProjectId(project.id);
    try {
      await addMedia.mutateAsync({ mediaIds, projectIds: [project.id] });
      const projectName = project.name || 'Untitled project';
      toast.success(
        skippedCount > 0
          ? `Added to ${projectName}. Skipped ${skippedCount} non-media item${skippedCount === 1 ? '' : 's'}.`
          : `Added to ${projectName}`,
        {
          action: {
            label: 'Open project',
            onClick: () => navigate(`/projects/${project.id}?tab=media`),
          },
        },
      );
      onAssigned?.(project.id, projectName);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't add to project");
    } finally {
      setPendingProjectId(null);
    }
  };

  // Inline quick-create: create a media-only (custom-kind) project named from
  // the picker's query, then assign the pending selection to it immediately —
  // the selection is never discarded (CEO review B1.3). The old navigate
  // fallback remains for an empty query.
  const createProjectAndAssign = async (name: string): Promise<void> => {
    const trimmed = name.trim().slice(0, 80);
    if (!trimmed) return;
    setPendingProjectId(CREATING_PROJECT_ID);
    try {
      const result = await campaignService.createCampaign({
        userId: '',
        name: trimmed,
        kind: 'custom',
      });
      if (result.error || !result.data) {
        throw new Error(result.error?.message ?? "Couldn't create the project");
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects', 'assignable'] }),
        queryClient.invalidateQueries({ queryKey: ['projects', 'list-for-save'] }),
        queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
      ]);
      await assign({ id: result.data.id, name: result.data.name } as ProjectRow);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't create the project");
    } finally {
      setPendingProjectId(null);
    }
  };

  return {
    mediaIds,
    projects: projectsQuery.data ?? [],
    loading: projectsQuery.isLoading,
    pendingProjectId,
    isChecked,
    assign,
    createProject: () => navigate('/?mode=plan'),
    createProjectAndAssign,
  };
}

function ProjectDropdownItem({
  project,
  checked,
  pending,
  onSelect,
}: {
  project: ProjectRow;
  checked: boolean;
  pending: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault();
        onSelect();
      }}
      disabled={pending}
      className={cn(MENU_ITEM_MOBILE, 'min-h-11 rounded-sm md:min-h-0', checked && 'opacity-80')}
    >
      <ProjectGlyph project={project} compact />
      <span className="truncate flex-1 text-foreground/85" title={project.name || undefined}>
        {project.name || 'Untitled project'}
      </span>
      {checked || pending ? (
        <CheckIcon className="size-3.5 shrink-0 text-foreground" weight="bold" />
      ) : project.updated_at ? (
        <span className="whitespace-nowrap text-detail text-muted-foreground/70">
          {formatWhen(project.updated_at)}
        </span>
      ) : null}
    </DropdownMenuItem>
  );
}

function ProjectGlyph({
  project,
  compact,
}: {
  project: Pick<ProjectRow, 'icon' | 'color'>;
  compact?: boolean;
}) {
  const ProjectIcon = getProjectIcon(project.icon ?? undefined);
  const colorConfig = getProjectColor(project.color ?? undefined);

  if (compact) {
    return (
      <ProjectIcon className={cn('size-3.5 shrink-0', colorConfig.textClass)} weight="regular" />
    );
  }

  return (
    <span
      className={cn(
        'flex size-7 shrink-0 items-center justify-center rounded-md',
        colorConfig.bgClass,
      )}
    >
      <ProjectIcon className={cn('size-3.5', colorConfig.textClass)} weight="regular" />
    </span>
  );
}
